import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosHeaders, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { handleApiError } from './api-error-handler'; // Referencing the error handler we created earlier
import { toast } from 'sonner';
import useAuthStore from '@/features/auth/stores/auth.store';

interface ApiResponse<T = any> {
  data: T;
  meta?: {
    total: number;
    page: number;
    per_page: number;
  };
}

interface ApiRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Static flag to prevent multiple 401 handlers from firing
let isHandling401 = false;

type QueueItem = {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
};

export class BaseApiService {
  protected readonly api: AxiosInstance;
  protected readonly baseUrl: string;
  private isRefreshing = false;
  private failedQueue: QueueItem[] = [];

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.api = axios.create({
      baseURL: baseUrl,
      timeout: 30000,
      headers: new AxiosHeaders({
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }),
      // Enable credentials for CORS
      withCredentials: true,
      // Ensure proper CORS handling
      validateStatus: status => status >= 200 && status < 300
    });

    this.setupInterceptors();
  }

  private processQueue(error: any = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });

    this.failedQueue = [];
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers = config.headers || new AxiosHeaders();
          config.headers.set('Authorization', `Bearer ${token}`);
        }

        // Ensure headers are properly set for CORS
        config.headers = config.headers || new AxiosHeaders();
        config.headers.set('Access-Control-Allow-Credentials', 'true');
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as ApiRequestConfig;
        
        // Handle CORS errors with more specific messaging
        if (error.message?.includes('Network Error') || error.code === 'ERR_NETWORK') {
          console.error('CORS Error Details:', {
            error,
            config: originalRequest,
            headers: originalRequest?.headers
          });
          toast.error('Connection Error', {
            description: 'Unable to connect to the server. Please check your network connection.',
          });
          return Promise.reject(error);
        }

        // Only retry once and only for network errors or timeouts
        if (!originalRequest._retry && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
          originalRequest._retry = true;
          try {
            return await this.api(originalRequest);
          } catch (retryError) {
            return Promise.reject(retryError);
          }
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                if (!originalRequest.headers) {
                  originalRequest.headers = new AxiosHeaders();
                }
                originalRequest.headers.set('Authorization', `Bearer ${token}`);
                return this.api(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Clear tokens and redirect to home
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            useAuthStore.setState({ isAuthenticated: false });
            toast.error('Session Expired', {
              description: 'Your session has expired. Please log in again.',
            });
            window.location.href = '/';

            return Promise.reject(error);
          } catch (refreshError) {
            this.failedQueue.forEach((prom) => {
              prom.reject(refreshError);
            });

            // Clear tokens and redirect to home
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            useAuthStore.setState({ isAuthenticated: false });
            toast.error('Session Expired', {
              description: 'Your session has expired. Please log in again.',
            });
            window.location.href = '/';

            return Promise.reject(refreshError);
          } finally {
            this.failedQueue = [];
            this.isRefreshing = false;
          }
        } else if (error.response?.status === 500) {
          toast.error('Server Error', {
            description: 'Something went wrong on our end. Please try again later.',
          });
        } else if (!error.response) {
          // CORS error or network error
          toast.error('Connection Error', {
            description: 'Unable to connect to the server. Please check CORS configuration.',
          });
          console.error('CORS or Network Error:', error);
        }
        return Promise.reject(error);
      }
    );
  }

  private async refreshAccessToken(refreshToken: string): Promise<AxiosResponse> {
    return this.api.post('/auth/refresh', {
      refresh_token: refreshToken,
    });
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.get<T>(url, config);
      return {
        data: response.data,
        meta: response.data && typeof response.data === 'object' && 'meta' in response.data
          ? (response.data as any).meta
          : undefined
      };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.post<T>(url, data, {
        ...config,
        headers: {
          ...config?.headers,
          'Content-Type': config?.headers?.['Content-Type'] || 'application/json'
        }
      });
      return {
        data: response.data,
        meta: response.data && typeof response.data === 'object' && 'meta' in response.data
          ? (response.data as any).meta
          : undefined
      };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      console.log('Making PUT request:', {
        url: this.api.defaults.baseURL + url,
        data,
        config
      });
      const response = await this.api.put<T>(url, data, config);
      console.log('PUT request successful:', response);
      return {
        data: response.data,
        meta: response.data && typeof response.data === 'object' && 'meta' in response.data
          ? (response.data as any).meta
          : undefined
      };
    } catch (error) {
      console.error('PUT request failed:', {
        url: this.api.defaults.baseURL + url,
        error: error instanceof Error ? error.message : error
      });
      throw handleApiError(error);
    }
  }

  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    try {
      const response = await this.api.patch<T>(url, data, config);
      return {
        data: response.data,
        meta: response.data && typeof response.data === 'object' && 'meta' in response.data
          ? (response.data as any).meta
          : undefined
      };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  protected async delete(url: string, config?: AxiosRequestConfig): Promise<void> {
    try {
      await this.api.delete(url, config);
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

export default BaseApiService;